import { useQuery, useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAxios } from './use-axios';

export interface ReportMetadata {
  id: string;
  name: string;
  description: string;
  path: string;
}

// This would normally come from an API, but for now it's hardcoded
const AVAILABLE_REPORTS: ReportMetadata[] = [
  {
    id: 'contacts-by-domain-status',
    name: 'Contacts by Domain Status',
    description: 'List of contacts filtered by domain status, TLD, and contact type',
    path: '/contacts-by-domain-status',
  },
  {
    id: 'deals-won-lost',
    name: 'Deals Won/Lost Report',
    description: 'Analysis of deal outcomes including stages, types, and dates',
    path: '/deals-won-lost',
  },
  {
    id: 'marketing-contacts',
    name: 'Marketing Contacts Report',
    description: 'Comprehensive report of final contacts categorized by activity status (active/inactive) and organizational oddities.',
    path: '/marketing-contacts',
  },
  {
    id: 'fdic-licenses',
    name: 'FDIC Licenses Report',
    description: 'Report of all registrants and their unique licenses, with focus on FDIC licenses for .bank domains.',
    path: '/fdic-licenses',
  },
  {
    id: 'deals-missing-outcome',
    name: 'Deals Missing Outcome',
    description: 'All deals not in the "Migrated" stage that have no outcome recorded, oldest first.',
    path: '/deals-missing-outcome',
  },
  {
    id: 'batch-calendar',
    name: 'Batch Calendar Report',
    description: 'Comprehensive calendar of all verification batches showing detailed domain information, contacts, and licenses.',
    path: '/batch-calendar',
  },
  {
    id: 'report-builder',
    name: 'Report Builder',
    description: 'Build custom reports from Monitor domain data with flexible filters, presets, and Excel export.',
    path: '/report-builder',
  },
  {
    id: 'bank-cumulative',
    name: '.Bank Cumulative Report',
    description: 'Monthly .bank statistics grid by year: new domains, activations, DUM, registrants, applications, and rates. Live-computed current period with editable historical cells.',
    path: '/bank-cumulative',
  },
  {
    id: 'state-adoption',
    name: 'State Adoption Report',
    description: 'Per-state .bank adoption: FDIC-insured banks vs. unique registrants vs. live .bankers, with potential new registrant and activation counts.',
    path: '/state-adoption',
  },
  // {
  //   id: 'domain-activity',
  //   name: 'Domain Activity Report',
  //   description: 'Detailed activity log for all domains including registration and renewal dates',
  //   path: '/domain-activity',
  // },
];

export function useReports() {
  return useQuery({
    queryKey: ['reports'],
    queryFn: async () => AVAILABLE_REPORTS,
  });
}

export interface ReportExecutionParams {
  reportId: string;
  filters: Record<string, unknown>;
}

const API_BASE_URL = '/reports';

/**
 * API Contract for Contacts by Domain Status Report
 *
 * Request:
 * POST /reports/contacts-by-domain-status
 * {
 *   domainStatus: string[];  // Array of statuses: 'live', 'parked', 'redirect_in_zone', 'redirect_out_of_zone', 'inactive'
 *   tld: string[];          // Array of TLDs: '.Bank', '.Insurance'
 *   contactType: string[];  // Array of contact types: 'registrant', 'verification'
 * }
 *
 * Response:
 * {
 *   data: {
 *     contacts: Array<{
 *       name: string;
 *       email: string;
 *       phone: string;
 *       organization: string;
 *     }>;
 *   }
 * }
 *
 * Error Response:
 * {
 *   error: {
 *     message: string;
 *     status: number;
 *   }
 * }
 */

interface ApiError {
  error?: {
    message?: string;
    status?: number;
  };
  details?: Array<{ message: string }>;
}

interface Contact {
  name: string;
  email: string;
  phone: string;
  organization: string;
}

interface ReportResponse {
  data: {
    contacts: Contact[];
  };
}

interface ContactsByDomainStatusParams {
  domainStatus: string[];
  tld: string[];
  contactType: string[];
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError<ApiError>(error)) {
    // Handle validation errors
    if (error.response?.data?.details) {
      return 'Please check your input and try again';
    }

    // Handle specific error codes
    if (error.response?.status === 500) {
      return 'An internal server error occurred. Please try again later';
    }

    if (error.response?.status === 401 || error.response?.status === 403) {
      return 'You do not have permission to perform this action';
    }

    if (error.response?.status === 404) {
      return 'The requested report type was not found';
    }

    // Handle network errors
    if (error.message === 'Network Error') {
      return 'Unable to connect to the server. Please check your internet connection';
    }
  }

  return 'An unexpected error occurred. Please try again';
}

// Generate Contacts by Domain Status Report
export function useContactsByDomainStatusReport() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async (params: ContactsByDomainStatusParams) => {
      try {
        const { data } = await axios.post<ReportResponse>(
          `${API_BASE_URL}/contacts-by-domain-status`,
          params,
        );
        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
  });
}

// --- Marketing Contacts Report Types ---

export interface MarketingContact {
  legal_name: string | null;
  email: string | null;
  phone_number: string | null;
  organization_legal_name: string;
  country: string | null;
  state_province: string | null;
  domains: string[];
  type: string;
  us_bank?: string; // "Y", "N", or "" (empty string)
  fdic_licenses?: string; // Comma-separated FDIC license numbers
  holding_company_licenses?: string; // Comma-separated holding company license numbers
  other_licenses?: string; // Comma-separated other license information
  use_restriction?: string; // Comma-separated deduplicated use restrictions from domains
  use_restriction_note?: string; // Comma-separated deduplicated use restriction notes from domains
}

export interface OrganizationOddity {
  name: string;
  hasRegistrant: boolean;
  hasVerification: boolean;
  registrantEmail: string | null;
  verificationEmail: string | null;
}

// --- Batch Calendar Report Types ---

export interface BatchCalendarDomain {
  domain_name: string;
  tld: string | null;
  batch_criteria: string;
  batch_criteria_type: 'registrant_email' | 'license' | 'registrant_org_name';
  batch_size: number;
  oldest_verification: string;
  last_verified: string | null;
  last_deal_created_at: string | null;
  deal_stage: string | null;
  deal_status_by_stage: 'Active' | 'Inactive' | null;
  domain_status: 'Active' | 'Inactive' | null;
  domain_status_raw: string | null;
  deal_last_updated_at: string | null;
  most_recent_registrar: string;
  noavsend: boolean;
  use_restriction: string | null;
  use_restriction_note: string | null;
  registrant_legal_name: string | null;
  registrant_alt_name: string | null;
  registrant_email: string | null;
  registrant_alt_email: string | null;
  registrant_phone: string | null;
  registrant_organization: string | null;
  registrant_country: string | null;
  registrant_state_province: string | null;
  verification_legal_name: string | null;
  verification_alt_name: string | null;
  verification_email: string | null;
  verification_alt_email: string | null;
  verification_phone: string | null;
  verification_organization: string | null;
  verification_country: string | null;
  verification_state_province: string | null;
  fdic_licenses: string;
  holding_company_licenses: string;
  other_licenses: string;
}

export interface BatchCalendarSummary {
  criteria: string;
  criteria_type: 'registrant_email' | 'license' | 'registrant_org_name';
  domain_count: number;
  oldest_verification: string;
  domains: string[];
  regions: string[];
  deal_stage: string | null;
  deal_status_by_stage: 'Active' | 'Inactive' | 'Mixed' | null;
  domain_status: 'Active' | 'Inactive' | 'Mixed' | null;
}

export interface BatchCalendarResponse {
  domains: BatchCalendarDomain[];
  total_domains: number;
  total_batches: number;
  batch_summary: BatchCalendarSummary[];
  all_domains: Array<{ domain_name: string; last_verified: string | null }>;
}

export interface BatchCalendarParams {
  batch_by: 'registrant_email' | 'license' | 'registrant_org_name';
  filters?: {
    regions?: string[];
    registrars?: string[];
    emails?: string[];
    names?: string[];
    licenses?: Array<{ authority: string; value: string }>;
    domains?: string[];
    organizations?: string[];
    exclude_regions?: string[];
    exclude_registrars?: string[];
    exclude_emails?: string[];
    exclude_names?: string[];
    exclude_licenses?: Array<{ authority: string; value: string }>;
    exclude_domains?: string[];
    exclude_organizations?: string[];
    skip_rdds_check?: boolean;
    skip_noavsend?: boolean;
    include_failed_verifications?: boolean;
    activity_window_days?: number;
  };
}

export interface MarketingContactsResponse {
  activeContacts: MarketingContact[];
  inactiveContacts: MarketingContact[];
  organizationOddities: OrganizationOddity[];
}

export interface MarketingContactsParams {
  exclude_organizations?: string[]; // Optional array of organization legal names
}

// --- End Marketing Contacts Report Types ---

// --- FDIC Licenses Report Types ---

export interface FdicLicense {
  value: string;
  authority: string;
}

export interface FdicLicenseRow {
  email: string;
  organization_legal_name: string | null;
  country: string | null;
  state_province: string | null;
  fdic_license: string;
  use_restriction: string | null;
  use_restriction_note: string | null;
}

export interface FdicLicensesResponse {
  licenses: FdicLicenseRow[];
  total_licenses: number;
}

export interface FdicLicensesParams {
  // No additional parameters needed for this report
}

// --- End FDIC Licenses Report Types ---

// --- Deals Missing Outcome Report Types ---

export interface DealMissingOutcomeRow {
  id: string;
  domains: string[];
  deal_stage: string;
  deal_type: string;
  outcome: string | null;
  created_at: string;
  updated_at: string;
}

export interface DealsMissingOutcomeResponse {
  deals: DealMissingOutcomeRow[];
  total_deals: number;
}

// --- End Deals Missing Outcome Report Types ---

interface ReportResponse {
  data: {
    contacts: Contact[];
  };
}

// Generate Marketing Contacts Report
export function useMarketingContactsReport() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async (params: MarketingContactsParams) => {
      try {
        const { data } = await axios.post<MarketingContactsResponse>(
          `${API_BASE_URL}/marketing-contacts`,
          params,
        );
        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
  });
}

// Generate FDIC Licenses Report
export function useFdicLicensesReport() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async (params: FdicLicensesParams) => {
      try {
        const { data } = await axios.post<FdicLicensesResponse>(
          `${API_BASE_URL}/fdic-licenses`,
          params,
        );
        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
  });
}

// Deals Missing Outcome Report (loads on mount; no parameters)
export function useDealsMissingOutcomeReport() {
  const axios = useAxios();

  return useQuery({
    queryKey: ['reports', 'deals-missing-outcome'],
    queryFn: async () => {
      try {
        const { data } = await axios.post<DealsMissingOutcomeResponse>(
          `${API_BASE_URL}/deals-missing-outcome`,
          {},
        );
        return data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
  });
}

/**
 * Hook for generating batch calendar reports
 *
 * @returns Mutation hook for batch calendar report generation
 */
export function useBatchCalendarReport() {
  const axios = useAxios();

  return useMutation({
    mutationFn: async (params: BatchCalendarParams): Promise<BatchCalendarResponse> => {
      const response = await axios.post(`${API_BASE_URL}/batch-calendar`, params);
      return response.data;
    },
    onError: (error: unknown) => {
      if (isAxiosError(error)) {
        throw new Error(getErrorMessage(error));
      }
    },
  });
}

// This is a temporary function to maintain backward compatibility
// while we migrate to the new hook-based approach
export async function executeReport({ reportId, filters }: { reportId: string; filters: Record<string, unknown> }): Promise<string> {
  // Simulate API delay
  await new Promise((resolve) => setTimeout(resolve, 1000));
  // Return mock base64 data
  return 'bW9jayBiYXNlNjQgZGF0YQ==';
}
